/**
 * Order Scheduler — Cart integration
 * -----------------------------------
 * 1. Populates the order-info banners in cart drawer & cart page.
 * 2. Fills hidden cart-attribute fields so the order info reaches checkout.
 * 3. Also pushes cart attributes via the AJAX API on every scheduler change,
 *    so even if the user goes directly to /checkout the data is there.
 * 4. Time is only shown/sent for Delivery orders.
 * 5. CHECKOUT GATE: Blocks checkout if Order Type or Date is missing.
 * 6. PICKUP PRE-SELECT: Uses Storefront API to set delivery preference to
 *    "Pick up" so checkout auto-selects the correct option.
 */
(function () {
  'use strict';

  var OS = window.OrderScheduler;
  if (!OS) return;

  /* ── Config ────────────────────────────────────────────────── */
  var PICKUP_LOCATION_ID = '77542621345';
  var STOREFRONT_API_VERSION = '2024-10';

  /* ── Render order-info HTML into a container ─────────────────── */
  function renderInfoBox(el) {
    if (!el) return;
    if (!OS.getDate() && !OS.getType()) {
      el.style.display = 'none';
      return;
    }

    var rows = '';
    if (OS.getType()) {
      rows += '<div class="os-cart-info__row">' +
        '<span class="os-cart-info__label">Order Type:</span>' +
        '<span class="os-cart-info__value">' + OS.getType() + '</span></div>';
    }
    if (OS.getDate()) {
      rows += '<div class="os-cart-info__row">' +
        '<span class="os-cart-info__label">Date:</span>' +
        '<span class="os-cart-info__value">' + OS.getDate() + '</span></div>';
    }
    // Time is captured for both Pickup and Delivery (was Delivery-only). Show it
    // in the cart info banner whenever a time has been selected.
    if (OS.getTime()) {
      rows += '<div class="os-cart-info__row">' +
        '<span class="os-cart-info__label">Time:</span>' +
        '<span class="os-cart-info__value">' + OS.getTime() + '</span></div>';
    }
    if (OS.getType() === 'Delivery') {
      var notes = sessionStorage.getItem('cc_delivery_notes');
      if (notes) {
        rows += '<div class="os-cart-info__row">' +
          '<span class="os-cart-info__label">Instructions:</span>' +
          '<span class="os-cart-info__value os-cart-info__value--notes">' + notes + '</span></div>';
      }
    }
    rows += '<a href="/pages/order" class="os-cart-info__edit">Change</a>';

    el.innerHTML = rows;
    el.style.display = '';
  }

  /* ── Fill hidden cart-attribute fields ──────────────────────────
     Time is included for BOTH Pickup and Delivery (matches the line-item
     properties written by getLineProperties() in order-scheduler.js — was
     previously a Delivery-only guard, which left Pickup orders missing
     the time on the order's "Additional details" panel even though it
     was on each line item). {%- comment fix-2026-05-07 -%} */
  function fillCartAttributes() {
    var typeEl = document.getElementById('os-cart-attr-type');
    var dateEl = document.getElementById('os-cart-attr-date');
    var timeEl = document.getElementById('os-cart-attr-time');
    if (typeEl) typeEl.value = OS.getType();
    if (dateEl) dateEl.value = OS.getDate();
    if (timeEl) timeEl.value = OS.getTime() || '';

    document.querySelectorAll('.os-cart-attr-type').forEach(function (el) { el.value = OS.getType(); });
    document.querySelectorAll('.os-cart-attr-date').forEach(function (el) { el.value = OS.getDate(); });
    document.querySelectorAll('.os-cart-attr-time').forEach(function (el) { el.value = OS.getTime() || ''; });
  }

  /* ── Build the cart-note string ────────────────────────────────
     Composes the order's `note` value as:
       "Pickup/Delivery: <Type> • <Date> • <Time>\n\n<customer's typed note>"
     If the customer hasn't typed anything, only the schedule line is set.
     If the scheduler isn't ready, only the customer's note is preserved.
     Strips any previously-injected "Pickup/Delivery: ..." line from the
     textarea value so repeated calls don't double-prefix.

     This is what Renee/Chris see in the order's Notes field (top of the
     order detail page in Shopify admin) — they wanted pickup/delivery info
     surfaced there, not just in cart attributes / line-item properties. */
  function buildOrderNote(cart) {
    // === Schedule line ===========================================
    var bits = [];
    if (OS.getType()) bits.push(OS.getType());
    if (OS.getDate()) bits.push(OS.getDate());
    if (OS.getTime()) bits.push(OS.getTime());
    var schedulePart = bits.length ? 'Pickup/Delivery: ' + bits.join(' • ') : '';

    // === Allergy block ===========================================
    // When a `cart` object is provided (preferred), build the allergy lines
    // from `item.properties['Allergy Friendly']` so the block is rebuilt
    // fresh and cannot be clobbered by a textarea that's been wiped by
    // a previous note overwrite. Without a cart object (legacy callers),
    // we fall back to whatever's currently in the cart-page textarea so
    // we don't accidentally drop the block.
    var allergyPart = '';
    if (cart && Array.isArray(cart.items)) {
      var allergyLines = [];
      cart.items.forEach(function (item) {
        var allergy = item.properties && item.properties['Allergy Friendly'];
        if (allergy && allergy !== 'No allergy modifications') {
          var label = item.product_title || item.title;
          allergyLines.push('- ' + label + ': ' + allergy);
        }
      });
      if (allergyLines.length) {
        allergyPart = 'Allergy items:\n' + allergyLines.join('\n');
      }
    }

    // === Customer-typed text ====================================
    // Read from textarea if present (cart page), otherwise from cart.note
    // (cart drawer / direct checkout). In both cases, strip any prior
    // schedule prefix and allergy block so they don't get duplicated when
    // we re-emit below.
    function stripSystemBlocks(s) {
      return (s || '')
        .replace(/^Pickup\/Delivery:[^\n]*\n*/i, '')
        .replace(ALLERGY_BLOCK_RE, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    }
    var customerPart = '';
    var sawTextarea = false;
    document.querySelectorAll('textarea[name="note"]').forEach(function (el) {
      sawTextarea = true;
      var val = stripSystemBlocks(el.value);
      if (val.length > customerPart.length) customerPart = val;
    });
    if (!sawTextarea && cart && typeof cart.note === 'string') {
      customerPart = stripSystemBlocks(cart.note);
    }

    // === Assemble ===============================================
    var parts = [];
    if (schedulePart) parts.push(schedulePart);
    if (allergyPart) parts.push(allergyPart);
    if (customerPart) parts.push(customerPart);
    return parts.join('\n\n');
  }

  /* ── Push cart attributes via AJAX (so /checkout always has them) ── */
  function pushCartAttributes() {
    var attrs = {};
    // Always push Order Type if available (no date guard)
    if (OS.getType()) {
      attrs['Order Type'] = OS.getType();
    }
    if (OS.getDate()) {
      attrs['Pickup/Delivery Date'] = OS.getDate();
    }
    // Time is captured for BOTH Pickup and Delivery (was Delivery-only). Matches
    // getLineProperties() in order-scheduler.js so the cart-attribute summary
    // and the per-line-item properties don't disagree on Pickup orders.
    if (OS.getTime()) {
      attrs['Pickup/Delivery Time'] = OS.getTime();
    }
    // Only push if we have at least one attribute to send
    if (Object.keys(attrs).length === 0) return;

    // We DO NOT touch cart.note here — scheduler changes happen frequently and
    // any in-progress text the customer is typing in the cart-page note field
    // (sections/main-cart.liquid:194) must not be wiped. cart.note is set
    // exclusively at checkout time via pushCartAttributesSync() so it carries
    // both the schedule + the customer's final note.
    fetch((window.theme && window.theme.routes ? window.theme.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* silent fail — attributes are also in line items */ });
  }

  /* ── Push ONLY Order Type immediately (for instant persistence on type selection) ── */
  function pushTypeAttribute() {
    var type = OS.getType();
    if (!type) return;
    fetch((window.theme && window.theme.routes ? window.theme.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { 'Order Type': type } })
    }).catch(function () { /* silent */ });
  }

  /* ── Sync scheduling properties onto existing cart line items ──────
     Bug fix (2026-05-11): line item properties (Order Type, Pickup/Delivery
     Date, Pickup/Delivery Time) get baked in at add-to-cart time and don't
     update when the customer later changes their selection. The summary
     banner at the top of the cart updates correctly, but each line item
     still shows its original (now stale) Order Type / Date. That's
     confusing for the customer (Pickup banner + Delivery line items) AND
     for staff (admin order detail shows mixed info).
     This function rewrites the properties on every cart line item that
     already carries those properties to match the current sessionStorage.
     Jar cakes (Shipping: Nationwide) are skipped since they don't have a
     pickup/delivery schedule. */
  var _syncInFlight = false;
  function syncLineItemSchedulingProperties() {
    if (_syncInFlight) return Promise.resolve();
    _syncInFlight = true;
    var newType = OS.getType();
    var newDate = OS.getDate();
    var newTime = OS.getTime();
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Chain sequentially — Shopify's /cart/change.js can race when called
        // in parallel because the `line` index shifts as items reorder.
        var p = Promise.resolve();
        cart.items.forEach(function (item, idx) {
          if (item.properties && item.properties.Shipping === 'Nationwide') return;
          var props = Object.assign({}, item.properties || {});
          var changed = false;
          if (newType && props['Order Type'] && props['Order Type'] !== newType) {
            props['Order Type'] = newType; changed = true;
          }
          if (newDate && props['Pickup/Delivery Date'] && props['Pickup/Delivery Date'] !== newDate) {
            props['Pickup/Delivery Date'] = newDate; changed = true;
          }
          if (newTime && props['Pickup/Delivery Time'] && props['Pickup/Delivery Time'] !== newTime) {
            props['Pickup/Delivery Time'] = newTime; changed = true;
          }
          if (!changed) return;
          p = p.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ line: idx + 1, properties: props })
            }).catch(function () {});
          });
        });
        return p;
      })
      .then(function () {
        _syncInFlight = false;
        // Refresh cart drawer so updated properties are visible immediately.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function () { _syncInFlight = false; });
  }

  /* ═══════════════════════════════════════════════════════════════
     ALLERGY → ORDER NOTES SYNC
     The Allergy-Friendly products on the PDP capture the customer's
     allergy choice (Egg Free / GF + DF / etc.) as a LINE ITEM
     property — visible in admin but invisible in the standard CSV
     export. The /pages/bake-list parser reads from the CSV, so it
     can't see those selections.
     This function mirrors each line item's `Allergy Friendly`
     property into the order's `Notes` field (cart.note) as a clearly
     delimited block:
       Pickup/Delivery: Pickup • 13 May 2026 • 11:00 AM - 12:00 PM

       Allergy items:
       - Vanilla Peanut Butter Allergy-Friendly 6 Count Box: Gluten Friendly + Dairy Free

       <customer's typed note>
     The Notes field exports to the CSV's `Notes` column AND shows at
     the top of the admin order detail — much more visible to staff
     than cart attributes. The block is delimited so we can strip and
     re-add it cleanly when the cart changes (preserving any text the
     customer typed in the note textarea).
     ═══════════════════════════════════════════════════════════════ */
  var ALLERGY_BLOCK_RE = /\n*Allergy items:\n(?:- [^\n]+\n?)+/g;
  var _allergySyncInFlight = false;
  function syncAllergyToNote () {
    if (_allergySyncInFlight) return Promise.resolve();
    _allergySyncInFlight = true;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Use the single source-of-truth note builder so we always write the
        // FULL note (schedule + allergy block + customer text). This used to
        // write only the allergy block, leaving cart.note missing the
        // Pickup/Delivery prefix whenever syncAllergyToNote ran before
        // pushCartAttributesSync — which the order admin then exposed as a
        // bare "Allergy items: …" entry (e.g. order #1049 before this fix).
        var newNote = buildOrderNote(cart);
        var currentNote = cart.note || '';
        if (newNote === currentNote) return; // No change needed

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: newNote })
        }).then(function () {
          // Update any visible note textarea so the customer sees the change.
          document.querySelectorAll('textarea[name="note"]').forEach(function (el) {
            // Don't overwrite if the customer is actively typing in this field.
            if (document.activeElement === el) return;
            el.value = newNote;
          });
        });
      })
      .then(function () { _allergySyncInFlight = false; })
      .catch(function () { _allergySyncInFlight = false; });
  }

  /* ═══════════════════════════════════════════════════════════
     CHECKOUT GATE
     Blocks checkout if Order Type or Pickup/Delivery Date
     is missing from cart attributes / sessionStorage.
     ═══════════════════════════════════════════════════════════ */
  function hasRequiredOrderInfo() {
    var type = OS.getType();
    var date = OS.getDate();
    if (!type || !date) return false;
    // Type + date is sufficient for both Pickup and Delivery
    return true;
  }

  /**
   * Check cart for local (cupcake) vs nationwide (jar cake) items.
   * Returns a promise resolving to { hasLocal, hasNationwide }.
   */
  var _cartAnalysis = null;
  function analyzeCart() {
    if (_cartAnalysis) return Promise.resolve(_cartAnalysis);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasLocal = false;
        var hasNationwide = false;
        var has48hItem = false;
        cart.items.forEach(function (item) {
          if (item.properties && item.properties.Shipping === 'Nationwide') {
            hasNationwide = true;
          } else if (item.properties && item.properties._auto_added) {
            // Delivery fee — follows local items
          } else {
            hasLocal = true;
          }
          // Detect 48h items (cakes / pies / cookies — per the FAQ). Match
          // by SKU prefix because product_type is inconsistent in the catalog.
          // CAK- = cakes, PIE- = pies, COO- = cookies. Also catch the
          // strawberry shortcakes which use CAK-SSH* SKUs.
          var sku = (item.sku || '').toUpperCase();
          if (/^(CAK-|PIE-|COO-)/.test(sku)) {
            has48hItem = true;
          }
          // PATCH 2026-07-21 (cake-topper 48h): cake products sold with a
          // topper add-on are ordered via variants that currently have NO SKU
          // (the topper variants were created without SKUs), so the SKU test
          // above misses them and the cake slips back to the 24h default. A
          // topper is always a cake, so treat any topper variant as a 48h item.
          // Topper option values look like "6-Inch / Anniversary (Gold)",
          // "Mini / Birthday (Pink)", "10-Inch / Congratulations (Black)" — the
          // default is "No Topper". Match the topper name in the variant title.
          // TEMPORARY: remove once the topper variants are given real CAK- SKUs
          // (then the SKU test alone will cover them).
          var variantTitle = (item.variant_title || '').toLowerCase();
          if (/\b(anniversary|birthday|congratulations)\b/.test(variantTitle)) {
            has48hItem = true;
          }
          // PATCH 2026-07-21 (all cakes 48h): some cake products are NOT on the
          // `product.cake` template and/or have null-SKU variants (duplicate
          // products, missing SKUs), so the SKU test above misses them and they
          // slip back to 24h. Catch any cake by product title as a safety net.
          // Excludes cupcakes, cheesecakes, and jar cakes (different lead times).
          var ptitle = (item.product_title || item.title || '').toLowerCase();
          if (ptitle.indexOf('cake') > -1 && ptitle.indexOf('cupcake') === -1 && ptitle.indexOf('cheesecake') === -1 && ptitle.indexOf('jar cake') === -1) {
            has48hItem = true;
          }
        });
        _cartAnalysis = { hasLocal: hasLocal, hasNationwide: hasNationwide, has48hItem: has48hItem };
        // Reflect the 48h requirement onto any scheduler picker container that
        // doesn't already declare a stricter lead-time. This keeps the cart
        // drawer's date picker from offering tomorrow when a cake is in cart.
        if (has48hItem) {
          document.querySelectorAll('[data-min-lead-hours]').forEach(function (el) {
            var cur = parseInt(el.getAttribute('data-min-lead-hours'), 10) || 24;
            if (cur < 48) el.setAttribute('data-min-lead-hours', '48');
          });
          // Also tag the document body so any picker initialized later picks it up.
          document.body.setAttribute('data-cart-min-lead-hours', '48');
        }
        return _cartAnalysis;
      })
      .catch(function () { return { hasLocal: true, hasNationwide: false, has48hItem: false }; });
  }

  // Invalidate cache on cart changes
  document.addEventListener('line-item:change:end', function () {
    _cartAnalysis = null;
    document.body.removeAttribute('data-cart-min-lead-hours');
    // Re-analyze after the cart settles
    setTimeout(analyzeCart, 100);
  });

  // Initial analysis on page load — ensures the data-min-lead-hours hint is
  // already in place before the customer opens the cart's date picker.
  if (document.readyState !== 'loading') {
    analyzeCart();
  } else {
    document.addEventListener('DOMContentLoaded', analyzeCart);
  }

  function cartHasLocalProducts() {
    // Synchronous fallback — check if order info exists (assume local if so)
    return OS.getType() || OS.getDate();
  }

  function showCheckoutGate() {
    var existing = document.getElementById('os-checkout-gate');
    if (existing) existing.remove();

    var msg = _cartAnalysis && _cartAnalysis.hasNationwide
      ? 'Your cart includes cupcakes that need a pickup or delivery date. Your jar cakes will ship separately.'
      : 'Please select your order type and date so we can prepare your cupcakes fresh!';

    var modal = document.createElement('div');
    modal.id = 'os-checkout-gate';
    modal.innerHTML =
      '<div class="os-checkout-gate__overlay">' +
        '<div class="os-checkout-gate__modal">' +
          '<h3>Before you check out...</h3>' +
          '<p>' + msg + '</p>' +
          '<div class="os-checkout-gate__actions">' +
            '<a href="/pages/order" class="os-checkout-gate__btn os-checkout-gate__btn--primary">Start Your Order</a>' +
            '<button type="button" class="os-checkout-gate__btn os-checkout-gate__btn--ghost" id="os-gate-cancel">Cancel</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.querySelector('#os-gate-cancel').addEventListener('click', function () {
      modal.remove();
    });
    modal.querySelector('.os-checkout-gate__overlay').addEventListener('click', function (e) {
      if (e.target === this) modal.remove();
    });
  }

  /* ═══════════════════════════════════════════════════════════
     STOREFRONT API — Pickup delivery preference
     Uses cartBuyerIdentityUpdate to set deliveryMethod: PICK_UP
     so checkout auto-selects "Pick up" for Pickup orders.
     ═══════════════════════════════════════════════════════════ */

  /**
   * Extract the Storefront API access token from page scripts.
   * Shopify embeds this 32-char hex token via {{ content_for_header }}.
   */
  function getStorefrontToken() {
    var scripts = document.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var text = scripts[i].textContent;
      if (text.indexOf('accessToken') !== -1) {
        var match = text.match(/accessToken['":\s]+['"]([a-f0-9]{32})['"]/);
        if (match) return match[1];
      }
    }
    return null;
  }

  /**
   * Get the cart token from /cart.js — this is also the Storefront API cart ID.
   */
  function getCartToken() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) { return cart.token; });
  }

  /**
   * Call Storefront API cartBuyerIdentityUpdate to set delivery preference.
   * For Pickup: sets deliveryMethod to PICK_UP with the store's location handle.
   * For Delivery/Shipping: sets deliveryMethod to SHIPPING (default).
   * Returns a promise that resolves to the checkout URL.
   */
  function setDeliveryPreference(type) {
    var token = getStorefrontToken();
    if (!token) {
      // Fallback: just go to /checkout
      return Promise.resolve(null);
    }

    var shopDomain = (window.theme && window.theme.shopDomain) ||
                     (window.Shopify && window.Shopify.shop) ||
                     window.location.hostname;

    return getCartToken().then(function (cartToken) {
      if (!cartToken) return null;

      var cartId = 'gid://shopify/Cart/' + cartToken;

      var deliveryPrefs = {};
      if (type === 'Pickup') {
        deliveryPrefs.deliveryMethod = ['PICK_UP'];
        deliveryPrefs.pickupHandle = [PICKUP_LOCATION_ID];
      } else {
        deliveryPrefs.deliveryMethod = ['SHIPPING'];
      }

      var query = 'mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) { ' +
        'cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) { ' +
          'cart { id checkoutUrl } ' +
          'userErrors { code field message } ' +
        '} ' +
      '}';

      var variables = {
        cartId: cartId,
        buyerIdentity: {
          countryCode: 'US',
          preferences: {
            delivery: deliveryPrefs
          }
        }
      };

      return fetch('https://' + shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: query, variables: variables })
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.data &&
            data.data.cartBuyerIdentityUpdate &&
            data.data.cartBuyerIdentityUpdate.cart) {
          return data.data.cartBuyerIdentityUpdate.cart.checkoutUrl || null;
        }
        return null;
      })
      .catch(function () { return null; });
    });
  }

  /* ── Ensure cart attributes are synced before checkout ──────
     Runs at checkout-button click. Sets cart.note = schedule + customer note
     so the order's Notes field (top of the order detail page) carries both
     the Pickup/Delivery summary and anything the customer typed into the
     cart-page note textarea. Pre-existing customer text is preserved by
     buildOrderNote(); a previously-injected "Pickup/Delivery: ..." prefix
     is stripped before re-adding so we never double up. */
  function pushCartAttributesSync() {
    // Fetch the live cart FIRST so buildOrderNote() can scan cart.items
    // for Allergy Friendly line-item properties and re-emit the allergy
    // block. Without this fetch, we'd overwrite cart.note using only the
    // schedule + textarea content — clobbering any allergy info added on
    // the PDP when the customer skipped the cart page (Shop Pay, direct
    // /checkout, cart drawer → checkout). Bug repro: order #1044 had
    // "Allergy Friendly: Dairy Free" as a line-item property but an empty
    // Notes column in CSV export.
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updateUrl = (window.theme && window.theme.routes ? window.theme.routes.cart_update_url : '/cart/update') + '.js';
        return fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            note: buildOrderNote(cart),
            attributes: OS.getLineProperties()
          })
        });
      })
      .catch(function () {});
  }

  function proceedToCheckout(btn) {
    pushCartAttributesSync().then(function () {
      return setDeliveryPreference(OS.getType());
    }).then(function (checkoutUrl) {
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        var form = btn.closest('form');
        if (!form && btn.getAttribute('form')) {
          form = document.getElementById(btn.getAttribute('form'));
        }
        if (form) {
          if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
            var h = document.createElement('input');
            h.type = 'hidden';
            h.name = 'checkout';
            h.value = '';
            form.appendChild(h);
          }
          form.submit();
        } else {
          window.location.href = '/checkout';
        }
      }
    });
  }

  function interceptCheckout() {
    // Intercept all checkout buttons and forms.
    // NOTE: The "Before you check out..." gate modal has been removed — users
    // proceed straight to checkout regardless of whether Order Type / Date are
    // set. The intercept is still needed so we can (a) sync cart attributes
    // and (b) call the Storefront API to pre-select Pickup delivery when
    // applicable. If no order info is set we fall through to /checkout and
    // Shopify handles it normally.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="checkout"], .checkout-button, [href*="/checkout"], .shopify-payment-button button, .additional-checkout-buttons button');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      analyzeCart().then(function (analysis) {
        // No local items — skip attribute sync, go straight to checkout
        if (!analysis.hasLocal) {
          window.location.href = '/checkout';
          return;
        }
        // 48h check: cakes/pies/cookies require 48-hour notice. If the cart
        // contains any of these AND the current pickup/delivery date is less
        // than 48h out, block checkout and prompt the customer to update the
        // date. analyzeCart() also patches `data-min-lead-hours` so the next
        // popup opening will offer the corrected min date.
        if (analysis.has48hItem && OS.getDate() && typeof OS.getMinDate === 'function') {
          var storedDate = new Date(OS.getDate());
          storedDate.setHours(0, 0, 0, 0);
          var minDate = OS.getMinDate();
          if (!isNaN(storedDate) && storedDate < minDate) {
            alert(
              'Your cart includes cakes, pies, or cookies which require 48 hours’ notice.\n\n' +
              'Please update your pickup or delivery date to ' +
              minDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) +
              ' or later.'
            );
            // Open the picker so they can change the date
            if (window.OrderSchedulerUI && window.OrderSchedulerUI.openPopup) {
              OS.clearDate();
              OS.clearTime();
              window.OrderSchedulerUI.openPopup();
            }
            return;
          }
        }
        // Always proceed — delivery preference is set if Order Type exists,
        // otherwise falls through to default /checkout.
        proceedToCheckout(btn);
      });

      return false;
    }, true);

    // Also intercept form submissions
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;

      var isCartForm = form.id === 'cart-form';
      var isCheckoutForm = form.querySelector('[name="checkout"]') || (e.submitter && e.submitter.name === 'checkout');
      if (!isCartForm && !isCheckoutForm) return;

      e.preventDefault();
      e.stopPropagation();

      analyzeCart().then(function (analysis) {
        if (!analysis.hasLocal) {
          window.location.href = '/checkout';
          return;
        }
        proceedToCheckout(form.querySelector('[name="checkout"]') || form.querySelector('.checkout-button') || form);
      });

      return false;
    }, true);
  }

  /* ── Mixed cart banner ─────────────────────────────────────── */
  function renderMixedCartBanner() {
    var existing = document.getElementById('os-mixed-cart-banner');
    analyzeCart().then(function (analysis) {
      if (analysis.hasLocal && analysis.hasNationwide) {
        if (!existing) {
          var banner = document.createElement('div');
          banner.id = 'os-mixed-cart-banner';
          banner.style.cssText = 'background:#FFF3E0;border:1px solid #FFB74D;border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:14px;line-height:1.5;color:#5D4037;';
          banner.innerHTML =
            '<strong style="display:block;margin-bottom:4px;">Your cart has two order types</strong>' +
            'Jar cakes ship nationwide. Cupcakes require a local pickup or delivery date. ' +
            'Both will be processed together at checkout.';
          var cartInfo = document.getElementById('os-cart-page-info');
          if (cartInfo) {
            cartInfo.parentNode.insertBefore(banner, cartInfo.nextSibling);
          }
        }
      } else if (existing) {
        existing.remove();
      }
    });
  }

  /* ── Main update function ────────────────────────────────────── */
  function update() {
    renderInfoBox(document.getElementById('os-cart-drawer-info'));
    renderInfoBox(document.getElementById('os-cart-page-info'));
    fillCartAttributes();
    renderMixedCartBanner();
  }

  /* ── Init ────────────────────────────────────────────────────── */
  function init() {
    update();
    pushCartAttributes();
    pushTypeAttribute();   // Ensure Order Type is pushed even without a date
    syncAllergyToNote();   // Mirror Allergy Friendly line item props into cart.note
    interceptCheckout();
  }

  // Re-sync allergy info into the order note whenever the cart changes
  // (covers add-to-cart, quantity change, remove). Delay slightly so the
  // cart settles before we read it.
  //
  // NOTE on event coverage: the theme's product.js dispatches `cart:refresh`
  // ONLY on the 422-error branch of /cart/add.js — the success branch fires
  // `cart:item-added` instead (assets/product.js ~line 831). Without the
  // `cart:item-added` listener below, a PDP add-to-cart for a cake would
  // never trigger syncAllergyToNote, and cart.note would stay empty until
  // the customer happened to land on /cart (where init() runs syncAllergyToNote
  // again). Listening to all three events guarantees the allergy block lands
  // in cart.note immediately, regardless of which product flow the customer
  // came from (PDP, jar cakes, cupcake box wizard).
  document.addEventListener('cart:refresh', function () {
    setTimeout(syncAllergyToNote, 200);
  });
  document.addEventListener('cart:item-added', function () {
    setTimeout(syncAllergyToNote, 200);
  });
  document.addEventListener('line-item:change:end', function () {
    setTimeout(syncAllergyToNote, 200);
  });

  /* ── Events ──────────────────────────────────────────────────── */
  document.addEventListener('order-scheduler:change', update);
  document.addEventListener('order-scheduler:type-changed', function () {
    pushTypeAttribute();
    fillCartAttributes();
    // Legacy support: existing orders placed before 2026-05-11 still carry
    // Order Type / Pickup-Delivery Date as line item properties. Keep the
    // sync running so those carts stay consistent if the customer is mid-
    // session and changes their selection. New orders won't have those
    // line item properties, so this is a no-op for them.
    syncLineItemSchedulingProperties();
  });
  document.addEventListener('order-scheduler:confirmed', function () {
    update();
    pushCartAttributes();
    syncLineItemSchedulingProperties();
  });
  document.addEventListener('cart-drawer:open', function () {
    setTimeout(update, 100);
  });
  document.addEventListener('line-item:change:end', function () {
    setTimeout(update, 100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
